import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import Screen from '../Components/Screen';
import { Card, Button, Row, Col, Typography, Space } from 'antd';
import { CheckCircleOutlined, FolderOutlined, FileTextOutlined, TeamOutlined, BarChartOutlined, SettingOutlined, DashboardOutlined, CalendarOutlined, SafetyOutlined, DollarOutlined, QuestionCircleOutlined, ShoppingOutlined, ClockCircleOutlined, MailOutlined, UserOutlined, LineChartOutlined, ToolOutlined, BulbOutlined, BankOutlined } from '@ant-design/icons';
import { getUser, getTeamUrl } from '../Common';

const { Title, Paragraph, Text } = Typography;

const Welcome = ({ history, userPermissions }) => {
    const user = getUser();
    const userType = (user && user.type) || 'Basic';

    const hasMenuPermission = (key) => {
        if (!user) return false;
        if (!!user.is_super) return true;
        return userPermissions.includes('menu_' + key);
    };

    // Subcontractor welcome page
    if (userType === 'Subcontractor') {
        const subcontractorOptions = [
            {
                key: 'my_orders',
                icon: <ShoppingOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
                title: 'My Orders',
                description: 'View and manage your assigned orders and track progress',
                path: getTeamUrl('my-orders'),
                color: '#1890ff',
            },
            {
                key: 'rams_signing',
                icon: <SafetyOutlined style={{ fontSize: 48, color: '#52c41a' }} />,
                title: 'RAMS',
                description: 'Review and sign Risk Assessments & Method Statements',
                path: getTeamUrl('rams-signing'),
                color: '#52c41a',
            },
            {
                key: 'tbt_signing',
                icon: <FileTextOutlined style={{ fontSize: 48, color: '#fa8c16' }} />,
                title: 'Tool Box Talks',
                description: 'View and sign your assigned Toolbox Talk documents',
                path: getTeamUrl('tbt-signing'),
                color: '#fa8c16',
            },
            {
                key: 'timesheet_posting',
                icon: <ClockCircleOutlined style={{ fontSize: 48, color: '#722ed1' }} />,
                title: 'Timesheets',
                description: 'Submit and review your timesheet entries',
                path: getTeamUrl('timesheet_posting'),
                color: '#722ed1',
            },
        ].filter(option => hasMenuPermission(option.key));

        return (
            <Screen>
                <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
                    <div style={{ textAlign: 'center', marginBottom: 40 }}>
                        <Title level={2} style={{ marginBottom: 8 }}>Welcome, {(user && user.name) || 'Subcontractor'}</Title>
                        <Paragraph style={{ fontSize: 16, color: '#666' }}>
                            Select an option below to get started.
                        </Paragraph>
                    </div>

                    <Row gutter={[24, 24]}>
                        {subcontractorOptions.map((option) => (
                            <Col xs={24} sm={12} key={option.key}>
                                <Card
                                    hoverable
                                    onClick={() => history.push(option.path)}
                                    style={{
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        borderTop: `3px solid ${option.color}`,
                                        height: '100%',
                                    }}
                                    bodyStyle={{ padding: '30px 24px' }}
                                >
                                    {option.icon}
                                    <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>{option.title}</Title>
                                    <Paragraph style={{ color: '#666', marginBottom: 0 }}>{option.description}</Paragraph>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            </Screen>
        );
    }

    // Define features - one tile for every menu item
    const allFeatures = [
        {
            key: 'dashboard',
            icon: <DashboardOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 15 }} />,
            title: 'Dashboard',
            description: 'Get a comprehensive overview of your business performance with real-time metrics and insights',
            path: getTeamUrl('dashboard'),
        },
        {
            key: 'projects',
            icon: <FolderOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 15 }} />,
            title: 'Projects',
            description: 'Organize and track all your construction projects from start to finish in one central location',
            path: getTeamUrl('projects'),
        },
        {
            key: 'estimates',
            icon: <FileTextOutlined style={{ fontSize: 48, color: '#fa8c16', marginBottom: 15 }} />,
            title: 'Estimates',
            description: 'Create detailed, professional estimates with itemized pricing and send them directly to clients',
            path: getTeamUrl('estimates'),
        },
        {
            key: 'orders',
            icon: <ShoppingOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 15 }} />,
            title: 'Orders',
            description: 'Convert estimates to orders and manage the entire order lifecycle from approval to completion',
            path: getTeamUrl('orders'),
        },
        {
            key: 'valuations',
            icon: <DollarOutlined style={{ fontSize: 48, color: '#13c2c2', marginBottom: 15 }} />,
            title: 'Valuations',
            description: 'Track project progress and create valuations for staged payments and interim billing',
            path: getTeamUrl('valuations'),
        },
        {
            key: 'invoices',
            icon: <DollarOutlined style={{ fontSize: 48, color: '#722ed1', marginBottom: 15 }} />,
            title: 'Invoices',
            description: 'Generate and send professional invoices, track payments, and manage your accounts receivable',
            path: getTeamUrl('invoices'),
        },
        {
            key: 'retention_payments',
            icon: <BankOutlined style={{ fontSize: 48, color: '#eb2f96', marginBottom: 15 }} />,
            title: 'Retentions',
            description: 'Track retention payments, release dates, and manage retention schedules across projects',
            path: getTeamUrl('retention_payments'),
        },
        {
            key: 'rams_records',
            icon: <SafetyOutlined style={{ fontSize: 48, color: '#f5222d', marginBottom: 15 }} />,
            title: 'RAMS',
            description: 'Create and manage risk assessments and method statements to ensure site safety compliance',
            path: getTeamUrl('rams'),
        },
        {
            key: 'clients',
            icon: <TeamOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 15 }} />,
            title: 'Clients',
            description: 'Maintain a comprehensive database of your clients with contact details and project history',
            path: getTeamUrl('clients'),
        },
        {
            key: 'suppliers',
            icon: <ShoppingOutlined style={{ fontSize: 48, color: '#a0d911', marginBottom: 15 }} />,
            title: 'Suppliers',
            description: 'Manage your supplier contacts, track orders, and maintain supplier relationships',
            path: getTeamUrl('suppliers'),
        },
        {
            key: 'subcontractors',
            icon: <TeamOutlined style={{ fontSize: 48, color: '#2f54eb', marginBottom: 15 }} />,
            title: 'Subcontractors',
            description: 'Manage your subcontractor network, assign work, and track their orders and payments',
            path: getTeamUrl('subcontractors'),
        },
        {
            key: 'planner',
            icon: <CalendarOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 15 }} />,
            title: 'Planner',
            description: 'Schedule tasks, allocate resources, and visualize project timelines with Gantt charts',
            path: getTeamUrl('planner'),
        },
        {
            key: 'timesheets',
            icon: <ClockCircleOutlined style={{ fontSize: 48, color: '#9254de', marginBottom: 15 }} />,
            title: 'Timesheets',
            description: 'Track labor hours, monitor productivity, and analyze time spent across projects and teams',
            path: getTeamUrl('timesheets'),
        },
        {
            key: 'profit_and_loss',
            icon: <LineChartOutlined style={{ fontSize: 48, color: '#389e0d', marginBottom: 15 }} />,
            title: 'Profit & Loss',
            description: 'Monitor financial performance with profit and loss reporting across all projects',
            path: getTeamUrl('profit_and_loss'),
        },
        {
            key: 'toolbox_talks',
            icon: <ToolOutlined style={{ fontSize: 48, color: '#d48806', marginBottom: 15 }} />,
            title: 'Tool Box Talks',
            description: 'Create, manage, and track toolbox talk sessions for your team and subcontractors',
            path: getTeamUrl('toolbox_talks'),
        },
        {
            key: 'insights',
            icon: <BulbOutlined style={{ fontSize: 48, color: '#08979c', marginBottom: 15 }} />,
            title: 'Insights',
            description: 'Access advanced analytics and insights to make data-driven business decisions',
            path: getTeamUrl('insights'),
        },
        {
            key: 'users',
            icon: <UserOutlined style={{ fontSize: 48, color: '#531dab', marginBottom: 15 }} />,
            title: 'Users',
            description: 'Manage team members, assign roles, and control access permissions',
            path: getTeamUrl('users'),
        },
        {
            key: 'settings',
            icon: <SettingOutlined style={{ fontSize: 48, color: '#595959', marginBottom: 15 }} />,
            title: 'Settings',
            description: 'Configure your account, company details, and system preferences',
            path: getTeamUrl('settings'),
        },
        {
            key: 'emails',
            icon: <MailOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 15 }} />,
            title: 'Emails',
            description: 'View and manage email communications sent from the system',
            path: getTeamUrl('emails'),
        },
    ];

    const allActions = [
        {
            key: 'dashboard',
            icon: <DashboardOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
            title: 'Explore Dashboard',
            description: 'View your dashboard and get familiar with the interface',
            path: getTeamUrl('dashboard'),
        },
        {
            key: 'projects',
            icon: <FolderOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
            title: 'Create Your First Project',
            description: 'Get started by setting up your first construction project',
            path: getTeamUrl('projects'),
        },
        {
            key: 'estimates',
            icon: <FileTextOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
            title: 'Create an Estimate',
            description: 'Generate your first professional estimate',
            path: getTeamUrl('estimates'),
        },
        {
            key: 'clients',
            icon: <TeamOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
            title: 'Add Clients',
            description: 'Start building your client database',
            path: getTeamUrl('clients'),
        },
        {
            key: 'users',
            icon: <UserOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
            title: 'Invite Team Members',
            description: 'Add your team to collaborate on projects',
            path: getTeamUrl('users'),
        },
        {
            key: 'settings',
            icon: <SettingOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
            title: 'Configure Settings',
            description: 'Customize your account and company settings',
            path: getTeamUrl('settings'),
        },
    ];

    // Filter features and actions based on menu permissions
    const availableFeatures = allFeatures.filter(feature => hasMenuPermission(feature.key));
    const availableActions = allActions.filter(action => hasMenuPermission(action.key));

    const handleComplete = () => {
        fetch('/user/complete-onboarding', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            }
        }).then(() => {
            // Redirect based on user type
            //if (userType === 'Advanced' || userType === 'Premium') {
                history.push(getTeamUrl('dashboard'));
            //} else {
            //    history.push(getTeamUrl('estimates'));
            //}
        });
    };

    return (
        <Screen>
            <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 20px' }}>
                <Card style={{ marginBottom: 30, textAlign: 'center' }}>
                    <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 20 }} />
                    <Title level={2}>Welcome to LeadPM!</Title>
                    <Paragraph style={{ fontSize: 16, color: '#666' }}>
                        Your account is ready. Let's get you started with the essentials.
                    </Paragraph>
                </Card>

                <Title level={3} style={{ marginBottom: 20 }}>What you can do</Title>
                <Row gutter={[16, 16]} style={{ marginBottom: 40 }}>
                    {availableFeatures.map((feature) => (
                        <Col
                            xs={24}
                            sm={12}
                            md={8}
                            lg={6}
                            key={feature.key}
                        >
                            <Card hoverable onClick={() => history.push(feature.path)} style={{ cursor: 'pointer' }}>
                                {feature.icon}
                                <Title level={4}>{feature.title}</Title>
                                <Paragraph>{feature.description}</Paragraph>
                            </Card>
                        </Col>
                    ))}
                </Row>

                <Title level={3} style={{ marginBottom: 20 }}>Quick Actions</Title>
                <Row gutter={[16, 16]} style={{ marginBottom: 40 }}>
                    {availableActions.map((action) => (
                        <Col xs={24} sm={12} lg={6} key={action.key}>
                            <Card hoverable onClick={() => history.push(action.path)} style={{ cursor: 'pointer' }}>
                                <Space>
                                    {action.icon}
                                    <div>
                                        <Title level={5} style={{ margin: 0 }}>{action.title}</Title>
                                        <Text type="secondary">{action.description}</Text>
                                    </div>
                                </Space>
                            </Card>
                        </Col>
                    ))}
                    <Col xs={24} sm={12} lg={6}>
                        <Card hoverable onClick={() => window.open('https://leadpm.cloud/help', '_blank')} style={{ cursor: 'pointer' }}>
                            <Space>
                                <QuestionCircleOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                                <div>
                                    <Title level={5} style={{ margin: 0 }}>Visit Help Center</Title>
                                    <Text type="secondary">Learn how to get the most out of LeadPM</Text>
                                </div>
                            </Space>
                        </Card>
                    </Col>
                </Row>

                <Card style={{ textAlign: 'center' }}>
                    <Space size="large">
                        <Button type="primary" size="large" onClick={handleComplete}>
                            Go to Dashboard
                        </Button>
                    </Space>
                    <div style={{ marginTop: 20 }}>
                        <Text type="secondary">
                            Need help getting started? {' '}
                            <a href="https://leadpm.cloud/help" target="_blank" rel="noopener noreferrer">Visit our Help Center</a>
                            {' '} or {' '}
                            <a href="mailto:support@leadpm.io">Contact Support</a>
                        </Text>
                    </div>
                </Card>
            </div>
        </Screen>
    );
};

const mapStateToProps = (state) => ({
    userPermissions: (state.user && state.user.permissions) || [],
});

export default connect(mapStateToProps)(withRouter(Welcome));
